import { useMemo } from 'react';

import { AlertCircle, GitBranch, Grid3x3, Loader2, RefreshCw, Wand2, XCircle } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useConfiguration } from '@/hooks/useConfigurations';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  useAdjustPromptFromScenarioRun,
  useCancelScenarioRun,
  useRerunFailedScenarios,
  useScenarioRun,
} from '@/hooks/useScenarioRuns';
import { ACTIVE_SCENARIO_RUN_STATUSES, channelLabel } from '@/lib/api/scenario-runs.api';
import { extractErrorMessage } from '@/lib/utils';
import { formatDateTime } from '@/utils/format';

import { CaseResultBadge, RunCounts, ScenarioRunStatusBadge, targetLabel } from './badges';
import { buildMatrix } from './matrix';

export const ScenarioRunDetailPage = () => {
  usePageTitle('Scenario Run');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: run, isLoading } = useScenarioRun(id);
  const { data: configuration } = useConfiguration(run?.configuration_id ?? '');
  const cancelMutation = useCancelScenarioRun();
  const rerunMutation = useRerunFailedScenarios();
  const adjustMutation = useAdjustPromptFromScenarioRun();

  const matrix = useMemo(() => buildMatrix(run?.cases ?? []), [run?.cases]);

  if (isLoading || !run) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  const isActive = ACTIVE_SCENARIO_RUN_STATUSES.has(run.status);
  const hasFailures = run.status === 'JUDGED' && run.failed > 0;
  const isMutating =
    cancelMutation.isPending || rerunMutation.isPending || adjustMutation.isPending;

  const handleCancel = async () => {
    try {
      await cancelMutation.mutateAsync(run.id);
      toast({ title: 'Cancelled', description: 'The scenario run has been cancelled.' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Cancel failed',
        description: extractErrorMessage(err),
      });
    }
  };

  const handleRerunFailed = async () => {
    try {
      const next = await rerunMutation.mutateAsync(run.id);
      toast({ title: 'Re-run started', description: 'Failed pairs are running again.' });
      navigate(`/agent-improvement/scenario-runs/${next.id}`);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Re-run failed',
        description: extractErrorMessage(err),
      });
    }
  };

  const handleAdjust = async () => {
    try {
      const { pipeline_run_id } = await adjustMutation.mutateAsync(run.id);
      toast({ title: 'Pipeline run created', description: 'The prompt is being adjusted.' });
      navigate(`/agent-improvement/runs/${pipeline_run_id}`);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Adjust failed',
        description: extractErrorMessage(err),
      });
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Grid3x3 className="h-5 w-5" />
            <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Scenario run</p>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{run.id.slice(0, 8)}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <ScenarioRunStatusBadge status={run.status} />
            <Badge variant="outline" className="font-mono text-xs">
              {targetLabel(run)}
            </Badge>
            <Link
              to={`/configurations/${run.configuration_id}`}
              className="text-primary text-xs hover:underline"
            >
              {configuration?.name ?? run.configuration_id.slice(0, 8)}
            </Link>
            <span className="text-muted-foreground text-xs">{formatDateTime(run.created_at)}</span>
          </div>
          <RunCounts run={run} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isActive && (
            <Button
              variant="outline"
              size="sm"
              disabled={isMutating}
              onClick={() => void handleCancel()}
            >
              <XCircle className="mr-1.5 h-4 w-4" />
              Cancel
            </Button>
          )}
          {hasFailures && (
            <Button
              variant="outline"
              size="sm"
              disabled={isMutating}
              onClick={() => void handleRerunFailed()}
            >
              <RefreshCw className="mr-1.5 h-4 w-4" />
              Re-run failed
            </Button>
          )}
          {hasFailures && !run.pipeline_run_id && (
            <Button size="sm" disabled={isMutating} onClick={() => void handleAdjust()}>
              <Wand2 className="mr-1.5 h-4 w-4" />
              Adjust the prompt
            </Button>
          )}
          {run.pipeline_run_id && (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/agent-improvement/runs/${run.pipeline_run_id}`}>
                <GitBranch className="mr-1.5 h-4 w-4" />
                Pipeline run
              </Link>
            </Button>
          )}
          <Button variant="outline" size="sm" asChild>
            <Link to="/agent-improvement/scenario-runs">Back to runs</Link>
          </Button>
        </div>
      </div>

      {run.status === 'FAILED' && run.error && (
        <Card className="border-destructive">
          <CardContent className="flex items-start gap-3 pt-4">
            <AlertCircle className="text-destructive mt-0.5 h-5 w-5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium">Run failed</p>
              <p className="text-muted-foreground mt-1 text-sm break-all">{run.error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Results</CardTitle>
        </CardHeader>
        <CardContent>
          {matrix.rows.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center text-sm">
              {isActive ? 'Preparing cases...' : 'No cases in this run.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="px-3 py-2 text-left font-medium">Scenario</th>
                    {matrix.columns.map((ch) => (
                      <th
                        key={`${ch.transport}/${ch.modality}`}
                        className="px-3 py-2 text-center font-medium whitespace-nowrap"
                      >
                        {channelLabel(ch)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.rows.map((row) => (
                    <tr key={row.scenarioId ?? row.scenarioName} className="border-b last:border-0">
                      <td className="px-3 py-2">
                        {row.scenarioId ? (
                          <Link
                            to={`/agent-improvement/scenarios/${row.scenarioId}`}
                            className="text-primary hover:underline"
                          >
                            {row.scenarioName}
                          </Link>
                        ) : (
                          <span>{row.scenarioName}</span>
                        )}
                      </td>
                      {row.cells.map((cell, i) => (
                        <td key={i} className="px-3 py-2 text-center">
                          {cell ? (
                            <Link to={`/agent-improvement/replays/${cell.id}`}>
                              <CaseResultBadge
                                result={cell.result}
                                status={cell.status}
                                className="cursor-pointer"
                              />
                            </Link>
                          ) : (
                            <span className="text-muted-foreground text-xs">&mdash;</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
